import { LEGACY_RSVP_STATUS_TO_RESPONSE } from './attendance.js';
const count = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
/** How many people a guest row stands for (0 when nothing is filled in). */
export function partySize(guest) {
    return count(guest.maleCount) + count(guest.femaleCount) + count(guest.kidCount);
}
/** Adults in the party, whatever their split. */
export function partyAdults(guest) {
    return count(guest.maleCount) + count(guest.femaleCount);
}
/** "First Last", or just the first name when there is no last. */
export function guestDisplayName(guest) {
    return [guest.firstName, guest.lastName]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' ');
}
/**
 * The four answers, in the order screens list them. The labels replace the
 * four spellings that grew up separately ("confirmed", "Accepted",
 * "Attending", "Yes") so a guest reads the same everywhere.
 */
export const RSVP_RESPONSES = [
    { value: 'YES', label: 'Attending', tone: 'success' },
    { value: 'NO', label: 'Not attending', tone: 'danger' },
    { value: 'MAYBE', label: 'Maybe', tone: 'warning' },
    { value: 'PENDING', label: 'Awaiting reply', tone: 'neutral' },
];
const RESPONSE_VALUES = new Set(RSVP_RESPONSES.map((option) => option.value));
/** True for one of the four `GuestRsvp` responses. */
export function isRsvpResponse(value) {
    return typeof value === 'string' && RESPONSE_VALUES.has(value);
}
/**
 * The legacy `Guest.rsvpStatus` spelling of a response. `GuestRsvp` is the
 * answer; this is the mirror kept on the guest row so seating, exports and
 * dashboards that read the old column stay in step.
 */
export const RESPONSE_TO_LEGACY_STATUS = {
    YES: 'confirmed',
    NO: 'declined',
    MAYBE: 'maybe',
    PENDING: 'invited',
};
/** A legacy status (or an unknown spelling) read as a response. */
export function legacyStatusToResponse(status) {
    return (status && LEGACY_RSVP_STATUS_TO_RESPONSE[status.toLowerCase()]) || 'PENDING';
}
/**
 * Is this floor-plan object a table guests can be seated at? A table number
 * alone is not enough — a numbered object with no seats cannot take anyone.
 */
export function isSeatableTable(object) {
    return object.tableNumber != null && (object.seats ?? 0) > 0;
}
/** Trimmed, de-duplicated, in the order given — how a list of tags is kept. */
export function normalizeTags(values) {
    const seen = new Set();
    const out = [];
    for (const value of values ?? []) {
        const tag = value.trim();
        if (tag && !seen.has(tag)) {
            seen.add(tag);
            out.push(tag);
        }
    }
    return out;
}
/** The tag that means VIP — `Guest.isVip` is kept in step with it. */
export const VIP_TAG = 'vip';
/**
 * A pair of guests in the order a `GuestRelation` stores it — the smaller id
 * first — so "A with B" and "B with A" are one row.
 */
export function orderedGuestPair(a, b) {
    return a < b ? [a, b] : [b, a];
}
// ─── Telling one party from another ──────────────────────────────────────
/** An email as two entries of it should compare: trimmed, lower case. */
export function normalizeEmail(value) {
    const email = value?.trim().toLowerCase();
    return email ? email : null;
}
/**
 * A phone number as two entries of it should compare: digits only, and the
 * last ten of them, so "+1 (305) 555-0100" and "305.555.0100" are one number.
 * Too few digits to be a number is no number.
 */
export function normalizePhone(value) {
    const digits = (value ?? '').replace(/\D/g, '');
    if (digits.length < 7)
        return null;
    return digits.slice(-10);
}
/** A name as two entries of it should compare: letters and single spaces, lower case. */
export function normalizePersonName(first, last) {
    const name = `${first ?? ''} ${last ?? ''}`
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
    return name || null;
}
/** The keys a guest is recognised by — its emails, phones and name. */
export function guestMatchKeys(guest) {
    const keys = [];
    const add = (reason, value) => {
        if (value && !keys.some(([r, v]) => r === reason && v === value))
            keys.push([reason, value]);
    };
    add('email', normalizeEmail(guest.email));
    add('phone', normalizePhone(guest.phone));
    for (const contact of guest.contacts ?? []) {
        if (contact.contactType === 'EMAIL')
            add('email', normalizeEmail(contact.value));
        if (contact.contactType === 'PHONE')
            add('phone', normalizePhone(contact.value));
    }
    add('name', normalizePersonName(guest.firstName, guest.lastName));
    return keys;
}
/**
 * Guests that look like one party entered more than once: any two sharing an
 * email, a phone or a whole name, joined transitively (A shares an email with
 * B, B a phone with C — all three are one group). Each group lists why.
 * Groups keep the order the guests were given in.
 */
export function findDuplicateGroups(guests) {
    const parent = guests.map((_, i) => i);
    const find = (i) => {
        while (parent[i] !== i) {
            parent[i] = parent[parent[i]];
            i = parent[i];
        }
        return i;
    };
    const firstWithKey = new Map();
    const reasonsByRoot = new Map();
    const joins = [];
    guests.forEach((guest, i) => {
        for (const [reason, value] of guestMatchKeys(guest)) {
            const key = `${reason}:${value}`;
            const first = firstWithKey.get(key);
            if (first === undefined)
                firstWithKey.set(key, i);
            else
                joins.push([first, i, reason]);
        }
    });
    for (const [a, b] of joins) {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb)
            parent[Math.max(ra, rb)] = Math.min(ra, rb);
    }
    for (const [a, , reason] of joins) {
        const root = find(a);
        const reasons = reasonsByRoot.get(root) ?? new Set();
        reasons.add(reason);
        reasonsByRoot.set(root, reasons);
    }
    const members = new Map();
    guests.forEach((guest, i) => {
        const root = find(i);
        if (!reasonsByRoot.has(root))
            return;
        members.set(root, [...(members.get(root) ?? []), guest]);
    });
    const order = ['email', 'phone', 'name'];
    return [...members.entries()].map(([root, group]) => ({
        reasons: order.filter((r) => reasonsByRoot.get(root)?.has(r)),
        guests: group,
    }));
}
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const PHONE_RE = /^\+?[\d\s().-]{7,}$/;
/**
 * Read one typed or pasted line as a guest: "David Levi, david@x.com, 2",
 * "Levi family 4", "Moshe Katz\t+1 305 555 0100", "Moshe & Rivka Levi +3
 * moshe@x.com". Pieces are split on commas, semicolons and tabs; an email, a
 * phone number and a small whole number (the party) are recognised wherever
 * they are, inside a piece as well as on their own. "+N" is N people beyond
 * those named, and "A & B" (or "A and B") names two. What is left is the name:
 * its first word the first name and the rest the last — or, for a couple, the
 * last word the surname and the rest the first names. A line with no name is
 * no guest.
 */
export function parseQuickGuest(line) {
    const pieces = line
        .split(/[,;\t]/)
        .map((piece) => piece.trim())
        .filter(Boolean);
    const guest = {};
    let extra;
    const nameWords = [];
    for (const piece of pieces) {
        if (!guest.email && EMAIL_RE.test(piece)) {
            guest.email = piece;
            continue;
        }
        if (!guest.phone && PHONE_RE.test(piece) && normalizePhone(piece)) {
            guest.phone = piece;
            continue;
        }
        if (guest.partySize === undefined && /^\d{1,2}$/.test(piece) && Number(piece) >= 1) {
            guest.partySize = Number(piece);
            continue;
        }
        const words = piece.split(/\s+/).filter((word) => {
            if (EMAIL_RE.test(word)) {
                guest.email ??= word;
                return false;
            }
            if (extra === undefined && /^\+\d{1,2}$/.test(word)) {
                extra = Number(word.slice(1));
                return false;
            }
            return true;
        });
        // A name piece may still end in its party size: "Levi family 4".
        const last = words[words.length - 1];
        if (guest.partySize === undefined && words.length > 1 && /^\d{1,2}$/.test(last)) {
            guest.partySize = Number(last) || undefined;
            words.pop();
        }
        nameWords.push(...words);
    }
    if (nameWords.length === 0)
        return null;
    const joiners = nameWords.filter((word) => word === '&' || word.toLowerCase() === 'and').length;
    if (guest.partySize === undefined && (joiners > 0 || extra)) {
        guest.partySize = 1 + joiners + (extra ?? 0);
    }
    const couple = joiners > 0 && nameWords.length > 2;
    const firstName = couple ? nameWords.slice(0, -1).join(' ') : nameWords[0];
    const rest = couple ? nameWords.slice(-1) : nameWords.slice(1);
    return {
        firstName,
        ...(rest.length ? { lastName: rest.join(' ') } : {}),
        ...(guest.email ? { email: guest.email } : {}),
        ...(guest.phone ? { phone: guest.phone } : {}),
        ...(guest.partySize ? { partySize: Math.min(guest.partySize, 50) } : {}),
    };
}
//# sourceMappingURL=guest.js.map